import React, {useMemo} from 'react';
import clsx from 'clsx';
import {MenuDropdownItemConfig, MenuLeafItemConfig} from '@common/menus/menu-config';
import {Trans} from '@ui/i18n/trans';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {ButtonBase} from '@ui/buttons/button-base';
import {ArrowDropDownIcon} from '@ui/icons/material/ArrowDropDown';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useLocation} from 'react-router';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {CustomMenuProps} from '@common/menus/custom-menu';

interface Props {
  item: MenuDropdownItemConfig;
  itemClassName?: CustomMenuProps['itemClassName'];
}

export function NavDropdownMenuItem({item, itemClassName}: Props) {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const isActive = useMemo(() => {
    return (item.items ?? []).some(subItem => {
      if (!subItem.action || isAbsoluteUrl(subItem.action)) {
        return false;
      }
      return (
        pathname === subItem.action ||
        pathname.startsWith(`${subItem.action}/`)
      );
    });
  }, [item.items, pathname]);

  const className =
    typeof itemClassName === 'function'
      ? itemClassName({isActive, item})
      : itemClassName;

  const handleItemClick = (subItem: MenuLeafItemConfig) => {
    if (isAbsoluteUrl(subItem.action)) {
      window.open(subItem.action, subItem.target)?.focus();
      return;
    }
    navigate(subItem.action);
  };

  return (
    <MenuTrigger placement="bottom-start">
      <ButtonBase
        className={clsx(
          className,
          'flex items-center gap-4 whitespace-nowrap outline-none focus-visible:ring-2',
        )}
        data-menu-item-id={item.id}
      >
        <Trans message={item.label} />
        <ArrowDropDownIcon className="icon-sm" />
      </ButtonBase>
      <Menu>
        {(item.items ?? []).map(subItem => {
          const Icon = subItem.icon && createSvgIconFromTree(subItem.icon);
          return (
            <MenuItem
              key={subItem.id}
              value={subItem.action}
              startIcon={Icon ? <Icon /> : undefined}
              onSelected={() => handleItemClick(subItem)}
            >
              <Trans message={subItem.label} />
            </MenuItem>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}
